import type { GpxTrace } from './gpx-parser';
import { GpxParser } from './gpx-parser';
import type { GpsPosition } from '../sensors/gps';

export interface GpsSimulatorOptions {
  onPosition: (position: GpsPosition) => void;
  onError: (error: GeolocationPositionError) => void;
  playbackSpeed?: number; // Multiplicateur de vitesse (1 = temps réel)
}

export class GpsSimulator {
  private trace: GpxTrace | null = null;
  private currentIndex = 0;
  private intervalId: number | null = null;
  private options: GpsSimulatorOptions;
  private isPlaying = false;
  private startTime: number = 0; // Timestamp du début de la simulation
  private traceStartTime: number = 0; // Timestamp du premier point de la trace
  
  constructor(options: GpsSimulatorOptions) {
    this.options = {
      playbackSpeed: 1,
      ...options
    };
  }
  
  loadTrace(trace: GpxTrace): void {
    // D'abord filtrer les segments stationnaires
    let processedTrace = GpxParser.filterStationarySegments(trace);
    
    // Puis optimiser les très grosses traces en gardant un point sur N
    if (processedTrace.points.length > 10000) {
      const skipFactor = Math.ceil(processedTrace.points.length / 10000);
      const optimizedPoints = processedTrace.points.filter((_, index) => index % skipFactor === 0);
      processedTrace = { ...processedTrace, points: optimizedPoints };
      console.log(`Trace optimisée: ${optimizedPoints.length} points (1 point sur ${skipFactor})`);
    }
    
    this.trace = processedTrace;
    this.currentIndex = 0;
    console.log(`Trace GPX chargée: ${processedTrace.name} (${processedTrace.points.length} points)`);
    
    // Démarrer automatiquement la lecture
    this.play();
  }
  
  play(): void {
    if (!this.trace || this.trace.points.length === 0) {
      console.error('Aucune trace GPX chargée');
      return;
    }
    
    if (this.isPlaying) return;
    
    this.isPlaying = true;
    
    // Ne redémarrer que si on n'était pas en pause
    if (this.startTime === 0 || this.currentIndex === 0) {
      this.startTime = Date.now();
      this.traceStartTime = this.trace.points[0].timestamp;
      console.log('Démarrage simulation GPX...');
    } else {
      console.log('Reprise simulation GPX...');
    }
    
    this.playNextPoint();
  }
  
  pause(): void {
    if (this.intervalId) {
      clearTimeout(this.intervalId);
      this.intervalId = null;
    }
    this.isPlaying = false;
    console.log('Simulation GPX en pause');
  }

  stop(): void {
    if (this.intervalId) {
      clearTimeout(this.intervalId);
      this.intervalId = null;
    }
    this.isPlaying = false;
    this.currentIndex = 0; // Reset à zéro pour stop complet
    console.log('Simulation GPX arrêtée');
  }

  setPlaybackSpeed(speed: number): void {
    this.options.playbackSpeed = speed;
    console.log(`Vitesse de simulation changée: x${speed}`);
  }
  
  private playNextPoint(): void {
    if (!this.trace || !this.isPlaying || this.currentIndex >= this.trace.points.length) {
      console.log('Fin de la trace GPX');
      this.stop();
      return;
    }
    
    const currentPoint = this.trace.points[this.currentIndex];
    const nextPoint = this.trace.points[this.currentIndex + 1];
    
    // Calculer le timestamp relatif au début de la simulation
    const traceElapsed = currentPoint.timestamp - this.traceStartTime;
    const simulatedTimestamp = this.startTime + traceElapsed;
    
    // Convertir le point GPX en GpsPosition
    const gpsPosition: GpsPosition = {
      lat: currentPoint.lat,
      lon: currentPoint.lon,
      accuracy: 5, // Précision simulée de 5m
      speed: currentPoint.speed || 0, // Vitesse réelle du GPX (peut être 0 pour les arrêts)
      heading: null, // Pas de heading dans le GPX, sera calculé par heading.ts
      timestamp: simulatedTimestamp // Timestamp relatif pour respecter les intervalles GPX
    };
    
    
    // Envoyer la position
    if (this.currentIndex % 20 === 0) { // Log tous les 20 points pour éviter le spam
      console.log(`🚂 GPS Simulator: Point ${this.currentIndex}/${this.trace.points.length} - Lat: ${gpsPosition.lat.toFixed(6)}, Speed: ${(gpsPosition.speed || 0).toFixed(1)} m/s`);
    }
    this.options.onPosition(gpsPosition);
    
    this.currentIndex++;
    
    // Calculer le délai jusqu'au point suivant
    if (nextPoint) {
      let realTimeInterval = nextPoint.timestamp - currentPoint.timestamp;
      
      // Si pas de timestamps valides dans le GPX, utiliser un intervalle par défaut
      if (realTimeInterval <= 0 || realTimeInterval > 300000) { // > 5 minutes = suspect
        realTimeInterval = 1000; // 1 seconde par défaut
      }
      
      const speedFactor = this.options.playbackSpeed || 1;
      const simulatedInterval = realTimeInterval / speedFactor;
      
      // Nouvelle logique : respecter le timing GPX mais avec fluidité minimale
      let minUpdateInterval: number;
      
      if (speedFactor === 1) {
        minUpdateInterval = 500; // Maximum 2fps pour x1 - respecter le timing réel
      } else if (speedFactor === 5) {
        minUpdateInterval = 100; // Maximum 10fps pour x5
      } else if (speedFactor === 10) {
        minUpdateInterval = 50; // 20fps pour x10
      } else if (speedFactor >= 20) {
        minUpdateInterval = 33; // 30fps pour x20+
      } else {
        minUpdateInterval = 67; // 15fps par défaut
      }
      
      // Pour x1 et x5, privilégier le timing GPX réel avec un minimum de fluidité
      // Pour x10+, privilégier la fluidité
      const finalInterval = speedFactor <= 5 
        ? Math.max(minUpdateInterval, simulatedInterval)  // Respecter le timing GPX
        : Math.max(minUpdateInterval, Math.min(100, simulatedInterval)); // Fluidité + timing
      
      
      this.intervalId = window.setTimeout(() => {
        this.playNextPoint();
      }, finalInterval);
    } else {
      // Dernier point
      this.stop();
    }
  }
  
  isActive(): boolean {
    // Actif si on joue OU si on est en pause (mais pas arrêté)
    return this.isPlaying || (this.trace !== null && this.currentIndex < (this.trace?.points.length || 0));
  }
  
  isPaused(): boolean {
    return !this.isPlaying && this.trace !== null && this.currentIndex < (this.trace?.points.length || 0);
  }
  
  getCurrentProgress(): { current: number; total: number; percentage: number } {
    const total = this.trace?.points.length || 0;
    const percentage = total > 0 ? (this.currentIndex / total) * 100 : 0;
    
    return {
      current: this.currentIndex,
      total,
      percentage: Math.round(percentage)
    };
  }
}